import type { WrapRootElementBrowserArgs, WrapRootElementNodeArgs } from 'gatsby';
import { Provider as ReduxProvider } from 'react-redux';
import { QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider } from '@printdeal/auth';
import { CartAuthenticationSync } from '@printdeal/checkout-frontend';
import { SegmentationProvider } from '@printdeal/segmentation/react';
import { Root } from '@printdeal/ui-library';
import { ConsentProvider, ConsentDataLayerSync } from '@printdeal/consent-management/react';
import { StrictMode } from 'react';
import { ConfigurationContext } from '@printdeal/configuration/react';
import type { Configuration } from '@printdeal/configuration/types';
import { actions as authActions } from '../store/slices/authSlice';
import store from '../store';
import SegmentScript from './segmentation/SegmentScript';
import SegmentProvider from './segmentation/SegmentProvider';
import SegmentationLogoutCoordinator from './segmentation/SegmentationLogoutCoordinator';
import AnalyticsTrackerProvider from './AnalyticsTracker/AnalyticsTrackerProvider';
import { getConfiguration } from '../helpers/Configuration';
import '../provide-configuration-values';
import AuthCompatibilityComponent from './authentication/AuthCompatibilityComponent';
import queryClient from '../queryClient';
import { MopinionScript } from './MopinionScript';
import _configurationFile from '../../static/configuration/configuration.json';

const RootElementWrapper = ({ element }: WrapRootElementBrowserArgs | WrapRootElementNodeArgs) => {
  // Immediately authenticate on first load
  store.dispatch(authActions.authenticate());

  return (
    <StrictMode>
      {/* icon-sprite.svg is located in ./static/generated/ */}
      <Root
        iconSpritePath="/generated/icons-sprite.svg"
        brandSpritePath="/generated/"
        brand={getConfiguration().channel.brand}
        brandSectionBgPrimaryPath="/generated/section-branded-asset-primary.svg"
        brandSectionBgWhitePath="/generated/section-branded-asset-white.svg"
      >
        <ConfigurationContext.Provider value={_configurationFile as Configuration}>
          <AuthProvider>
            <ReduxProvider store={store}>
              <QueryClientProvider client={queryClient}>
                <ConsentProvider providerId={process.env.GATSBY_COOKIEBOT_CBID as string}>
                  <SegmentProvider>
                    <SegmentationLogoutCoordinator>
                      <SegmentationProvider>
                        <AnalyticsTrackerProvider>
                          <CartAuthenticationSync />
                          {element}
                          <MopinionScript
                            deploymentId={getConfiguration().mopinion.deploymentId}
                            enabled={getConfiguration().mopinion.enabled}
                          />
                        </AnalyticsTrackerProvider>
                        <SegmentScript />
                      </SegmentationProvider>
                    </SegmentationLogoutCoordinator>
                  </SegmentProvider>
                  <AuthCompatibilityComponent />
                  <ConsentDataLayerSync />
                </ConsentProvider>
              </QueryClientProvider>
            </ReduxProvider>
          </AuthProvider>
        </ConfigurationContext.Provider>
      </Root>
    </StrictMode>
  );
};

export default RootElementWrapper;
